import { useEffect, useRef, type CSSProperties, type ReactElement } from 'react'
import styles from './DestinationButton.module.css'

/**
 * Which slot of the From → To pair this button represents.
 *
 * - `'from'` renders an "A" badge and uses the "From" semantic in accessible labels.
 * - `'to'` renders a "B" badge and uses the "To" semantic in accessible labels.
 */
export type DestinationSlot = 'from' | 'to'

/**
 * Layout variant for {@link DestinationButton}.
 *
 * Drives every size / affordance difference between mobile and desktop:
 * height, pill radius, label font, and X-clear icon visibility.
 *
 * - `'phone'` — 42px tall, 12px regular label, no X-clear icon (touch users
 *   use long-press / context menu instead).
 * - `'tablet'` — 52px tall, 14px medium label, X-clear icon visible when
 *   the slot is filled.
 */
export type DestinationButtonVariant = 'phone' | 'tablet'

/**
 * Minimal station shape consumed by {@link DestinationButton} when filled.
 *
 * The full station model lives upstream; this surface intentionally keeps only
 * the fields the pill needs so the component can be reused outside the
 * RouteCard store wiring.
 */
export interface DestinationButtonStation {
  /** Stable station identifier (used by callers for selection state). */
  readonly id: string
  /** Localised short display name (e.g. "Baker St"). */
  readonly shortLocalName: string
  /** Hex colour of the station's primary line, used as the pill background. */
  readonly lineHexColor: string
}

/**
 * Props for {@link DestinationButton}.
 *
 * The pill itself is rendered as `<div role="button">` (not `<button>`) so the
 * inner X clear affordance can be a real nested `<button>` without violating
 * the HTML "no nested interactive content" rule.
 */
export interface DestinationButtonProps {
  /** Which slot this button renders ('from' → "A", 'to' → "B"). */
  readonly slot: DestinationSlot
  /** Selected station, or `null` to render the empty placeholder state. */
  readonly station: DestinationButtonStation | null
  /** Placeholder text shown in the empty state (e.g. "From", "To"). */
  readonly placeholder: string
  /**
   * Layout variant — see {@link DestinationButtonVariant}.
   *
   * Drives height (42 vs 52), pill radius (21 vs 26), label font
   * (regular 12px vs medium 14px), and X-clear icon visibility (off vs on
   * when filled).
   */
  readonly variant: DestinationButtonVariant
  /**
   * Called when the user activates the X clear icon.
   *
   * Only invoked on the `tablet` variant when the slot is filled — that is
   * the only state in which the X is rendered. Phone variant suppresses the
   * X entirely (touch users clear via long-press / context menu).
   */
  readonly onClear?: () => void
  /**
   * Called when the user activates the pill body itself (click, Enter or
   * Space). Receives this button's {@link DestinationSlot}. Wired by the
   * host to open Suggester for the corresponding route slot
   * (TASK-318). When omitted the pill body is inert.
   *
   * The X clear control stops propagation, so a clear tap never also
   * fires `onTap`.
   */
  readonly onTap?: (slot: DestinationSlot) => void
  /**
   * Pills-as-search editing mode (TASK-412, side panel only). When
   * `true`, this pill IS the Suggester query input: the label slot
   * becomes a focused `<input>` (caret visible, autofocus) whose text is
   * the query. Inactive pills (and all phone-variant pills) leave this
   * `false` and render the static label. The host (`useSuggesterPanel`)
   * owns the query string and passes it via {@link query}; typing calls
   * {@link onQueryChange}. Defaults to `false`.
   */
  readonly editable?: boolean
  /** Controlled query text shown in the editable input. */
  readonly query?: string
  /** Fired on each keystroke while `editable` — the host updates the query. */
  readonly onQueryChange?: (query: string) => void
}

/**
 * Pill-style button rendering one slot of the From → To route picker.
 *
 * Three visual states:
 * - **Empty** (`station === null`): neutral background, gray "A"/"B" badge,
 *   placeholder text.
 * - **Filled** (`station !== null`): line-coloured background, white badge
 *   with the line colour as the badge text, station name as the label.
 * - **Filled + tablet variant**: same as filled, plus an X clear affordance
 *   (14×14 icon inside a 16×16 hit target). Clicking the X invokes
 *   {@link DestinationButtonProps.onClear} and stops propagation so the
 *   pill's own click handler (when one is wired in the future) doesn't
 *   also fire.
 */
export function DestinationButton({
  slot,
  station,
  placeholder,
  variant,
  onClear,
  onTap,
  editable = false,
  query = '',
  onQueryChange,
}: DestinationButtonProps): ReactElement {
  // While editing (pills-as-search), the live query supersedes the pinned
  // value: the active pill ALWAYS renders the empty/secondary skin (no
  // line-colour bg, hairline circle, caret+typed text = text/primary,
  // placeholder = text/secondary) even if this slot was previously filled
  // — cross-platform parity with iOS/Android (active pill = search input).
  const filled = station !== null && !editable
  const letter = slot === 'from' ? 'A' : 'B'
  const label = filled ? station.shortLocalName : placeholder
  // The Clear-X shows on the tablet variant when the pill is filled OR
  // while editing (TASK-412 §2): editing wires it to empty the query,
  // filled wires it to clear the slot. Hidden on an active+empty pill
  // (nothing to clear yet).
  const showClearIcon = variant === 'tablet' && (filled || (editable && query.length > 0))

  const slotName = slotLabel(slot)
  const accessibleLabel = filled
    ? `${slotName} station, currently ${station.shortLocalName}`
    : `${slotName} station, not selected`

  // Autofocus the input when this pill becomes the active query field so
  // the caret is visible and typing flows immediately (decision #1).
  const inputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (editable) inputRef.current?.focus()
  }, [editable])

  const buttonStyle: CSSProperties | undefined = filled
    ? { backgroundColor: station.lineHexColor }
    : undefined
  const circleStyle: CSSProperties | undefined = filled
    ? { color: station.lineHexColor }
    : undefined

  return (
    <div
      role="button"
      tabIndex={0}
      className={`${styles.button} ${styles[variant]} ${filled ? styles.filled : styles.empty}`}
      style={buttonStyle}
      aria-label={accessibleLabel}
      data-slot={slot}
      data-filled={filled}
      data-variant={variant}
      // While editing, the pill itself is not a tap target — the inner
      // input owns interaction (typing). Tapping/activating is only the
      // slot-switch affordance for the INACTIVE pill.
      onClick={editable ? undefined : () => onTap?.(slot)}
      onKeyDown={
        editable
          ? undefined
          : (e) => {
              // Restore native button keyboard activation — the pill is a
              // `<div role="button">` (so the inner X can be a real button),
              // which has no built-in Enter / Space handling.
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onTap?.(slot)
              }
            }
      }
      data-testid={slot === 'from' ? 'route-card-from-button' : 'route-card-to-button'}
    >
      <span className={styles.circle} style={circleStyle}>
        <span className={styles.letter}>{letter}</span>
      </span>
      {editable ? (
        <input
          ref={inputRef}
          type="text"
          className={`${styles.label} ${styles[`label_${variant}`]} ${styles.input}`}
          value={query}
          // No visible placeholder while active (TASK-412 §3): an
          // active+empty pill is caret-only (matches Figma pill A). The
          // a11y name still names the slot for screen readers.
          aria-label={`${slotName} station search`}
          // §8 contract id (TASK-412 ruling): in pills-as-search the
          // active pill IS the Suggester query input, so it carries the
          // role-based `suggester-search-field` id — identical to the
          // phone SearchField — so QA targets the query input the same way
          // on both surfaces. iOS ships keep-both (container pill id +
          // inner search-field id); web mirrors it (the parent div keeps
          // `route-card-<slot>-button`). `data-slot` disambiguates the
          // slot; the id stays unique since only one pill edits at a time.
          data-testid="suggester-search-field"
          data-slot={slot}
          onChange={(e) => onQueryChange?.(e.target.value)}
          // Keep keystrokes inside the input — the parent pill is inert
          // while editing, but stop propagation defensively so a future
          // parent handler can't hijack typing.
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <span className={`${styles.label} ${styles[`label_${variant}`]}`}>{label}</span>
      )}
      {showClearIcon && (
        <button
          type="button"
          className={styles.clearIcon}
          aria-label={`clear ${slotName}`}
          onClick={(e) => {
            e.stopPropagation()
            // While editing, the X empties the query (TASK-412 §2) and
            // keeps focus in the input; otherwise it clears the slot.
            if (editable) {
              onQueryChange?.('')
              inputRef.current?.focus()
            } else {
              onClear?.()
            }
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            aria-hidden="true"
            focusable="false"
            fill="none"
          >
            <path
              d="M3 3 L11 11 M11 3 L3 11"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button>
      )}
    </div>
  )
}

function slotLabel(slot: DestinationSlot): string {
  return slot === 'from' ? 'From' : 'To'
}
